import { toBerlinDateKey } from '@brimo/shared';
import { normalizeTimeInput } from '../../lib/time-input';

/**
 * Calendar-day helpers of the tasks module: day keys ('YYYY-MM-DD', Europe/Berlin), the short forms of
 * the screens („17.09.", „24.09.2026") and the month grid of the „Später" popover (Task-Queue 02). There
 * is no date library; every function works on keys and UTC-noon dates, never on local midnight.
 */

export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
] as const;

export interface DayParts {
  year: number;
  month: number;
  day: number;
}

export function parseDayKey(key: string): DayParts {
  const [year = 0, month = 0, day = 0] = key.split('-').map(Number);
  return { year, month, day };
}

export function toDayKey(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Today in Europe/Berlin. */
export function todayKey(): string {
  return toBerlinDateKey(new Date());
}

export function addDays(key: string, days: number): string {
  const { year, month, day } = parseDayKey(key);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return toDayKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

/** '2026-09-17' → „17.09." */
export function shortDay(key: string): string {
  const { month, day } = parseDayKey(key);
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.`;
}

/** '2026-09-24' → „24.09.2026" */
export function longDay(key: string): string {
  const { year, month, day } = parseDayKey(key);
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
}

/** „September 2026" */
export function monthLabel(year: number, month: number): string {
  return `${MONTHS[month - 1] ?? ''} ${year}`;
}

export function isValidDayKey(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const { year, month, day } = parseDayKey(value);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month && date.getUTCDate() === day;
}

export function isValidTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** „24.09.2026" (also „24.9.2026") → '2026-09-24'; null when the text is not a complete German date. */
export function parseGermanDate(text: string): string | null {
  const match = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s*$/.exec(text);
  if (match === null) return null;
  const key = toDayKey(Number(match[3]), Number(match[2]), Number(match[1]));
  return isValidDayKey(key) ? key : null;
}

/** „9:30", „930", „9.30", „9" → „09:30"/„09:00" (shared helper, D-441); other texts trimmed, unchanged. */
export function normalizeTime(text: string): string {
  return normalizeTimeInput(text);
}

export interface MonthCell {
  key: string | null;
  day: number;
  inMonth: boolean;
}

/**
 * The 7-column grid of a month starting on Monday: leading and trailing cells of the neighbouring
 * months carry `inMonth: false` and no key; five or six rows depending on the month.
 */
export function monthGrid(year: number, month: number): MonthCell[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const leading = (first.getUTCDay() + 6) % 7;
  const cells: MonthCell[] = [];
  for (let index = 0; index < leading; index += 1) cells.push({ key: null, day: 0, inMonth: false });
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ key: toDayKey(year, month, day), day, inMonth: true });
  }
  while (cells.length % 7 !== 0) cells.push({ key: null, day: 0, inMonth: false });
  return cells;
}

/** Relative day text for a key against today: „heute", „morgen", else „17.09.". */
export function relativeDay(key: string, today: string): string {
  if (key === today) return 'heute';
  if (key === addDays(today, 1)) return 'morgen';
  return shortDay(key);
}

/** „morgen um 08:30" / „am 02.10. um 09:00" / „morgen" — the sentence part of TQ-05 and Queue 11. */
export function dueSentence(on: string, time: string | null, today: string): string {
  const day = relativeDay(on, today);
  const dayText = day === 'heute' || day === 'morgen' ? day : `am ${day}`;
  return time === null ? dayText : `${dayText} um ${time}`;
}
